// Canonical export kernel — browser HostEnv for the Free SPA.
//
// Wraps `@nowline/export`'s `exportDocument` with a browser-native HostEnv
// (fetch-based I/O, resvg WASM loaded lazily from the asset bundle, bundled
// DejaVu fonts decoded with `atob()`). Every PNG and SVG export that goes to
// a file or the system clipboard routes through this module so output is
// byte-identical to the CLI and the VS Code extension for the same source and
// the same `@nowline/*` version.
//
// Spec: specs/export-determinism.md § What the apps must do for canonical export.
//
// Quick-grab (canvas rasterization in @nowline/preview-shell) is NOT replaced —
// it remains the non-canonical path for apps/tooling that need it. This module
// is the canonical path only.

import type { ExportFormat, HostEnv, RenderInputs, ThemeName } from '@nowline/export';
import { exportDocument } from '@nowline/export';
import { loadBundledFontsForBrowser } from '@nowline/export-core';
// Vite resolves this to a content-hashed URL; the file is copied to dist/.
// eslint-disable-next-line import/no-unresolved
import resvgWasmUrl from '@resvg/resvg-wasm/index_bg.wasm?url';

// ---- WASM cache -------------------------------------------------------------

let wasmCache: ArrayBuffer | undefined;

async function loadWasm(): Promise<ArrayBuffer> {
    if (!wasmCache) {
        const res = await fetch(resvgWasmUrl);
        if (!res.ok) throw new Error(`resvg.wasm fetch failed (${res.status})`);
        wasmCache = await res.arrayBuffer();
    }
    return wasmCache;
}

// ---- Browser HostEnv --------------------------------------------------------

/**
 * Build a browser HostEnv for a self-contained in-memory source.
 *
 * `readSource` returns the captured `source` string for any path (the Free
 * editor has no `include` directives referencing external files). Asset
 * references (e.g. inline-logo URLs) are fetched via `fetch()`.
 */
function createBrowserHost(source: string): HostEnv {
    return {
        readSource: async (_path: string) => source,
        readAsset: async (ref: string) => {
            const res = await fetch(ref);
            if (!res.ok) throw new Error(`Asset fetch failed: ${ref} (${res.status})`);
            return new Uint8Array(await res.arrayBuffer());
        },
        loadWasm,
    };
}

// ---- Canonical export -------------------------------------------------------

export interface CanonicalOpts {
    /** UTC-midnight date to draw the now-line. Omit to suppress the now-line. */
    today?: Date;
    /** BCP 47 locale tag for tick-label formatting. Defaults to `navigator.language`. */
    locale?: string;
    /** Diagram theme. Defaults to `'light'`. */
    theme?: ThemeName;
    /** PNG pixel-density multiplier (default 2). */
    pngScale?: number;
}

/**
 * Produce a canonical export artifact for `source` in `format`.
 *
 * Rasterizes PNG via `@resvg/resvg-wasm` with the bundled DejaVu fonts.
 * Byte-identical to `nowline -f <format>` for the same source and toolchain.
 */
export async function canonicalExport(
    source: string,
    format: ExportFormat,
    opts: CanonicalOpts = {},
): Promise<Uint8Array> {
    const fonts = loadBundledFontsForBrowser();
    const inputs: RenderInputs = {
        sourcePath: '/unnamed.nowline',
        ...(opts.today !== undefined ? { today: opts.today } : {}),
        locale: opts.locale ?? navigator.language,
        theme: opts.theme ?? 'light',
        fonts,
        pngScale: opts.pngScale ?? 2,
    };
    return exportDocument(source, format, inputs, createBrowserHost(source));
}

/**
 * Canonical PNG as a `Blob` — ready for `ClipboardItem({ 'image/png': blob })`.
 * The `Promise<Blob>` form is passed directly to `ClipboardItem` by
 * `@nowline/preview-shell` so WASM rasterization runs inside the user gesture.
 */
export async function canonicalPngBlob(source: string, opts: CanonicalOpts = {}): Promise<Blob> {
    const bytes = await canonicalExport(source, 'png', opts);
    return new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'image/png' });
}
